import { createContext, useContext, useState, useCallback, useEffect, useMemo, useRef } from "react";
import { useUserContext } from "./UserContext";
import { isUserSetUp } from "../utils/userUtils";

export interface TourStep {
  target: string;
  title: string;
  titleEs: string;
  content: string;
  contentEs: string;
  placement?: "top" | "bottom" | "left" | "right";
}

type TourLang = "en" | "es";

interface TourContextType {
  isActive: boolean;
  currentStep: number;
  steps: TourStep[];
  lang: TourLang;
  setLang: (lang: TourLang) => void;
  startTour: () => void;
  endTour: () => void;
  nextStep: () => void;
  prevStep: () => void;
  skipTour: () => void;
  resetTour: () => void;
}

const TourContext = createContext<TourContextType>({
  isActive: false,
  currentStep: 0,
  steps: [],
  lang: "en",
  setLang: () => {},
  startTour: () => {},
  endTour: () => {},
  nextStep: () => {},
  prevStep: () => {},
  skipTour: () => {},
  resetTour: () => {},
});

export const useTour = () => useContext(TourContext);

const ALL_TOUR_STEPS: TourStep[] = [
  {
    target: "[data-tour='search-bar']",
    title: "Search",
    titleEs: "Buscar",
    content: "Narrow this feed by product name, UPC, or SKU, and pin your saved keywords.",
    contentEs: "Filtra este feed por nombre, UPC o SKU y fija tus palabras clave guardadas.",
    placement: "bottom",
  },
  {
    target: "[data-tour='filters-button']",
    title: "Filters",
    titleEs: "Filtros",
    content: "Set price ranges, choose stores, toggle in-stock only, and more.",
    contentEs: "Establece rangos de precio, elige tiendas, filtra solo en stock y más.",
    placement: "bottom",
  },
  {
    target: "[data-tour='status-chip']",
    title: "Status",
    titleEs: "Estado",
    content: "Filter by New, Price Drops, Stock Changes, or your Saved items.",
    contentEs: "Filtra por Nuevos, Bajadas de Precio, Cambios de Stock o tus Guardados.",
    placement: "bottom",
  },
  {
    target: "[data-tour='product-card']",
    title: "Products",
    titleEs: "Productos",
    content: "Each card shows price and savings. Upgrade when you want exact nearby inventory, store prices, and aisle locations.",
    contentEs: "Cada tarjeta muestra precio, ahorro, stock y pasillo. Las etiquetas de color indican Nuevo, Bajada de Precio o cambio de Stock.",
    placement: "bottom",
  },
  {
    target: "[data-tour='product-expand']",
    title: "Details",
    titleEs: "Detalles",
    content: "Tap + to find nearby stores, set alerts, view the barcode, share, or open the retailer site.",
    contentEs: "Toca + para ver tiendas cercanas, alertas, código de barras, compartir o abrir el sitio del minorista.",
    placement: "left",
  },
  {
    target: "[data-tour='products']",
    title: "Navigation",
    titleEs: "Navegación",
    content: "Deals is your feed. Search, Leads, and Alerts are nearby-clearance tools; a lock means they unlock with an upgrade. More holds account settings.",
    contentEs: "Ofertas es tu inicio. Buscar, Oportunidades y Alertas son herramientas de ofertas locales; el candado indica que se desbloquean al mejorar el plan. Más contiene la configuración.",
    placement: "top",
  },
];

const getBrowserLang = (): TourLang => {
  const lang = navigator.language || "en";
  return lang.toLowerCase().startsWith("es") ? "es" : "en";
};

/**
 * `autoStart={false}` keeps the tour manual-only (the Filters help button):
 * first-time users get the "How to read a deal" guide on the deal grid instead.
 */
export function TourProvider({ children, autoStart = true }: { children: React.ReactNode; autoStart?: boolean }) {
  const { user, updateUser } = useUserContext();
  const [isActive, setIsActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [visibleSteps, setVisibleSteps] = useState<TourStep[]>([]);
  const [lang, setLang] = useState<TourLang>(getBrowserLang);
  const hasInitiatedRef = useRef(false);

  // Filter steps based on which targets exist in the DOM (prefer visible elements)
  const updateVisibleSteps = useCallback(() => {
    const visible = ALL_TOUR_STEPS.filter((step) => {
      const elements = document.querySelectorAll(step.target);
      if (elements.length === 0) return false;
      return Array.from(elements).some(el => {
        const rect = el.getBoundingClientRect();
        return rect.width > 0 && rect.height > 0 && rect.left >= 0 && rect.left <= window.innerWidth;
      });
    });
    setVisibleSteps(visible);
    return visible;
  }, []);

  // Check if tour should start on mount
  // Only show tour to users created on or after Jan 23, 2026
  const TOUR_LAUNCH_DATE = new Date('2026-01-23T00:00:00Z').getTime();
  
  useEffect(() => {
    const isNewUser = user?.created && user.created >= TOUR_LAUNCH_DATE;
    if (!autoStart) return;
    if (user && isUserSetUp(user) && !user.tourSeen && isNewUser && !hasInitiatedRef.current) {
      setTimeout(() => {
        const steps = updateVisibleSteps();
        if (steps.length > 0) {
          hasInitiatedRef.current = true;
          setIsActive(true);
        }
      }, 1500);
    }
  }, [autoStart, user, updateVisibleSteps]);

  // Update visible steps when tour becomes active
  useEffect(() => {
    if (isActive) updateVisibleSteps();
  }, [isActive, updateVisibleSteps]);

  const markTourSeen = useCallback(async () => {
    try {
      await updateUser({ tourSeen: true });
    } catch (e) {
      console.error("Failed to mark tour as seen:", e);
    }
  }, [updateUser]);

  const startTour = useCallback(() => {
    setCurrentStep(0);
    updateVisibleSteps();
    setIsActive(true);
  }, [updateVisibleSteps]);

  const endTour = useCallback(() => {
    setIsActive(false);
    setCurrentStep(0);
    markTourSeen();
  }, [markTourSeen]);

  const skipTour = useCallback(() => {
    setIsActive(false);
    setCurrentStep(0);
    markTourSeen();
  }, [markTourSeen]);

  const isStepVisible = useCallback((step: TourStep) => {
    const elements = document.querySelectorAll(step.target);
    if (elements.length === 0) return false;
    return Array.from(elements).some(el => {
      const rect = el.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0;
    });
  }, []);

  const nextStep = useCallback(() => {
    let next = currentStep + 1;
    while (next < visibleSteps.length && !isStepVisible(visibleSteps[next])) next++;
    if (next < visibleSteps.length) {
      setCurrentStep(next);
    } else {
      endTour();
    }
  }, [currentStep, visibleSteps, endTour, isStepVisible]);

  const prevStep = useCallback(() => {
    let prev = currentStep - 1;
    while (prev >= 0 && !isStepVisible(visibleSteps[prev])) prev--;
    if (prev >= 0) setCurrentStep(prev);
  }, [currentStep, visibleSteps, isStepVisible]);

  // Reset tour state to allow re-triggering
  const resetTour = useCallback(() => {
    hasInitiatedRef.current = false;
    setIsActive(false);
    setCurrentStep(0);
  }, []);

  const contextValue = useMemo(() => ({
    isActive, currentStep, steps: visibleSteps, lang, setLang, startTour, endTour, nextStep, prevStep, skipTour, resetTour
  }), [isActive, currentStep, visibleSteps, lang, startTour, endTour, nextStep, prevStep, skipTour, resetTour]);

  return (
    <TourContext.Provider value={contextValue}>
      {children}
    </TourContext.Provider>
  );
}
